import React, { useState } from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Grid,
  Heading,
  Text,
  Input,
  Textarea,
  Select,
  Button,
} from "@chakra-ui/react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import CTAButton from "../components/common/CTAButton";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaLinkedin, FaYoutube } from "react-icons/fa";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "student",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const db = window.firebase?.database?.();
      if (!db) {
        console.error("Firebase not initialized");
        setLoading(false);
        return;
      }

      const submissionId = Date.now().toString();
      const submission = {
        ...formData,
        submittedAt: Date.now(),
      };

      // Submit to Firebase Realtime Database
      await db.ref(`forms/contact/${submissionId}`).set(submission);

      console.log("Form submitted to Firebase:", submission);
      setSubmitted(true);
      setLoading(false);

      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData({ name: "", email: "", phone: "", role: "student", message: "" });
        setSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setLoading(false);
      alert("Error submitting form. Please try again.");
    }
  };

  const faqStudent = [
    {
      question: "How long is the program?",
      answer:
        "Most programs run for 3-6 months, depending on the discipline. You can start immediately and proceed at your own pace.",
    },
    {
      question: "Do I need prior experience?",
      answer:
        "No. We teach from basics. If you have passion and commitment, we'll get you job-ready.",
    },
    {
      question: "What's the success rate?",
      answer:
        "85%+ of our students land jobs within 3 months. Most get placed in roles matching their learning.",
    },
    {
      question: "Can I do this while studying?",
      answer:
        "Yes! Many students do this alongside their college. It typically takes 10-15 hours/week.",
    },
    {
      question: "Do you help with interviews?",
      answer:
        "Absolutely. Mock interviews, resume reviews, and interview tips are part of the program.",
    },
  ];

  const faqCollege = [
    {
      question: "Will this interfere with our curriculum?",
      answer:
        "No. We enhance your curriculum with practical skills. Your existing classes continue as normal.",
    },
    {
      question: "What's the partnership cost?",
      answer:
        "We work on a success-based model. You only pay a small fee per successful placement.",
    },
    {
      question: "How do you measure success?",
      answer:
        "Clear KPIs: placement rates, salary packages, student satisfaction, and enrollment growth.",
    },
    {
      question: "Can we customize the program?",
      answer:
        "Yes. We customize based on your college's needs, academic calendar, and student demographics.",
    },
    {
      question: "How do we get started?",
      answer:
        "Schedule a consultation. We'll understand your needs and propose a customized plan.",
    },
  ];

  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title="Let's Build Success Together"
        subtitle="Whether you're a student looking to launch your career or a college wanting to improve outcomes, we're here to help."
        badge="Get in Touch"
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <CTAButton to="/" variant="outline" size="lg">
            Back to Home
          </CTAButton>
        </HStack>
      </Hero>

      {/* Contact Info Section */}
      <Box py={{ base: 12, md: 20 }} bg="#F8FAFC">
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
            {/* Email */}
            <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0" textAlign="center">
              <Box fontSize="3xl" color="#10B981" mb={4}>
                <FaEnvelope />
              </Box>
              <Heading size="sm" color="#1E293B" mb={2}>
                Email
              </Heading>
              <Text color="#475569" mb={4}>
                info@ojasamirai.com
              </Text>
              <Text fontSize="sm" color="#64748B">
                For general inquiries and information
              </Text>
            </Box>

            {/* Phone */}
            <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0" textAlign="center">
              <Box fontSize="3xl" color="#10B981" mb={4}>
                <FaPhone />
              </Box>
              <Heading size="sm" color="#1E293B" mb={2}>
                Phone
              </Heading>
              <Text color="#475569" mb={4}>
                +91-9900-30-9000
              </Text>
              <Text fontSize="sm" color="#64748B">
                Mon-Fri 9 AM - 6 PM IST
              </Text>
            </Box>

            {/* Location */}
            <Box p={8} bg="white" borderRadius="lg" border="1px solid #E2E8F0" textAlign="center">
              <Box fontSize="3xl" color="#10B981" mb={4}>
                <FaMapMarkerAlt />
              </Box>
              <Heading size="sm" color="#1E293B" mb={2}>
                Address
              </Heading>
              <Text color="#475569" mb={4}>
                Bangalore, India
              </Text>
              <Text fontSize="sm" color="#64748B">
                Open to remote partnerships
              </Text>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.lg">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Send Us a Message
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Tell us about yourself. We'll get back to you within 24 hours.
              </Text>
            </VStack>

            <Box w="full" p={8} bg="#F8FAFC" borderRadius="lg" border="1px solid #E2E8F0">
              {submitted ? (
                <VStack spacing={4} textAlign="center">
                  <Box fontSize="4xl" color="#10B981">
                    ✓
                  </Box>
                  <Heading size="md" color="#1E293B">
                    Thank You!
                  </Heading>
                  <Text color="#475569">
                    We've received your message. Our team will get back to you within 24 hours.
                  </Text>
                </VStack>
              ) : (
                <form onSubmit={handleSubmit}>
                  <VStack spacing={6}>
                    {/* Name & Email Row */}
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} w="full">
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="600" color="#1E293B">
                          Full Name *
                        </Text>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          required
                          p={3}
                          bg="white"
                          borderColor="#E2E8F0"
                          _focus={{ borderColor: "#10B981" }}
                        />
                      </VStack>

                      <VStack align="start" spacing={2}>
                        <Text fontWeight="600" color="#1E293B">
                          Email *
                        </Text>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your.email@example.com"
                          required
                          p={3}
                          bg="white"
                          borderColor="#E2E8F0"
                          _focus={{ borderColor: "#10B981" }}
                        />
                      </VStack>
                    </Grid>

                    {/* Phone & Role Row */}
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} w="full">
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="600" color="#1E293B">
                          Phone *
                        </Text>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91-XXXXXXXXXX"
                          required
                          p={3}
                          bg="white"
                          borderColor="#E2E8F0"
                          _focus={{ borderColor: "#10B981" }}
                        />
                      </VStack>

                      <VStack align="start" spacing={2}>
                        <Text fontWeight="600" color="#1E293B">
                          I'm a... *
                        </Text>
                        <Select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          p={3}
                          bg="white"
                          borderColor="#E2E8F0"
                          _focus={{ borderColor: "#10B981" }}
                        >
                          <option value="student">Student</option>
                          <option value="faculty">Faculty Member</option>
                          <option value="college">College Administrator</option>
                          <option value="recruiter">Recruiter / Company</option>
                          <option value="other">Other</option>
                        </Select>
                      </VStack>
                    </Grid>

                    {/* Message */}
                    <VStack align="start" spacing={2} w="full">
                      <Text fontWeight="600" color="#1E293B">
                        Message *
                      </Text>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your needs, goals, or questions..."
                        required
                        rows={6}
                        p={3}
                        bg="white"
                        borderColor="#E2E8F0"
                        _focus={{ borderColor: "#10B981" }}
                      />
                    </VStack>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      isLoading={loading}
                      loadingText="Sending..."
                      w="full"
                      size="lg"
                      bg="#10B981"
                      color="white"
                      _hover={{ bg: "#0D9467" }}
                    >
                      Send Message
                    </Button>

                    <Text fontSize="sm" color="#64748B" textAlign="center">
                      We respect your privacy. Your information will never be shared.
                    </Text>
                  </VStack>
                </form>
              )}
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* FAQ Section - Two Columns */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Frequently Asked Questions
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Quick answers to common questions. Can't find what you're looking for?
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              {/* Student FAQ */}
              <Box>
                <Heading size="md" color="#1E293B" mb={6}>
                  For Students
                </Heading>
                <VStack spacing={3}>
                  {faqStudent.map((item, idx) => (
                    <Box key={idx} p={4} bg="white" borderRadius="lg" border="1px solid #E2E8F0" w="full">
                      <Heading size="xs" color="#1E293B" mb={2}>
                        {item.question}
                      </Heading>
                      <Text color="#475569" fontSize="sm">
                        {item.answer}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>

              {/* College FAQ */}
              <Box>
                <Heading size="md" color="#1E293B" mb={6}>
                  For Colleges
                </Heading>
                <VStack spacing={3}>
                  {faqCollege.map((item, idx) => (
                    <Box key={idx} p={4} bg="white" borderRadius="lg" border="1px solid #E2E8F0" w="full">
                      <Heading size="xs" color="#1E293B" mb={2}>
                        {item.question}
                      </Heading>
                      <Text color="#475569" fontSize="sm">
                        {item.answer}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Social Links Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12} textAlign="center">
            <VStack spacing={4}>
              <Heading as="h2" size="xl" color="#1E293B">
                Follow Us
              </Heading>
              <Text color="#475569" maxW="2xl">
                Connect with us on social media for updates, tips, and success stories.
              </Text>
            </VStack>

            <HStack spacing={8} justify="center">
              <Box
                p={4}
                bg="#F8FAFC"
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: "#10B981", color: "white" }}
                transition="all 0.3s"
              >
                <FaLinkedin fontSize="2xl" />
              </Box>
              <Box
                p={4}
                bg="#F8FAFC"
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: "#10B981", color: "white" }}
                transition="all 0.3s"
              >
                <FaYoutube fontSize="2xl" />
              </Box>
              <Box
                p={4}
                bg="#F8FAFC"
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: "#10B981", color: "white" }}
                transition="all 0.3s"
              >
                <FaEnvelope fontSize="2xl" />
              </Box>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
